import { GIButton, Modal } from 'app-components';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { ModalContextType } from '../../../../contexts/modalContext';
import Dropdown from '../../../../lib/inputs/Dropdown';
import DatePicker from '../../../../lib/inputs/react-datepicker';
import { formatDateToMMDDYYYY } from '../../../../utils/dates/dateMethods';
import CommonTextArea from '../../common/input/CommonTextArea';
import useGetOpenShoppingWindowDetails from '../../hooks/useGetOpenShoppingWindowDetails';
import {
    ConditionalRendering,
    DEFAULT_PERSON_ID,
    environmentSelector,
    INPUT_MAX_LENGTH,
    INPUT_TYPES,
    IShoppingWindowAllEvents,
    useAppSelector,
    validateCoverageAfterPlanYear,
    validateStartBeforeEndShoppingWindow,
    ValidationErrorMessage,
} from '../../utils';

const StyledField = styled.div`
    .date-picker,
    .shown.error--msg {
        margin-top: 4px !important;
        padding: 8px;
        width: 100%;
        background-color: ${({ theme }) => theme.dangerLightest};
    }

    .gi-input {
        text-align: left;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
    }
`;

const StyledPlanShoppingWindow = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 20px;
    padding-bottom: 10px;
    text-align: left;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 5px;
    }
`;

const StyledChangeCoverageStartDate = styled.div``;

const StyledModalControls = styled.div`
    @media (max-width: 768px) {
        .gi-modal__controls {
            margin-top: -82px;
            position: relative;
            z-index: 10;
        }
    }
`;

const StyledControls = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;

    @media (max-width: 768px) {
        padding-top: -100px;
        flex-direction: column;
        gap: 2px;
    }
`;

const ContentAlert = styled.div`
    background-color: ${({ theme }) => theme.infoLightest};
    padding: 8px 8px;
    border-radius: 2px;
    color: ${({ theme }) => theme['border-color-alert-info']};
    margin-bottom: 20px;
    a {
        text-decoration: underline;
        &:visited {
            color: ${({ theme }) => theme['bg-color-navbar-link-hover']};
        }
    }
`;
const StyledTable = styled.table`
    width: 100%;
    span {
        color: ${({ theme }) => theme.dangerMain};
        margin-left: 5px;
    }
    margin-bottom: 40px;

    th {
        background-color: ${({ theme }) => theme.gray10};
        padding: 10px;
        font-size: 17.5px;
        font-weight: 600;
    }

    td {
        padding: 0px 10px;
    }

    .eventReasonType {
        padding: 0 30px;
    }
`;

const StyledBodyHeader = styled.h4`
    padding: 10px;
    font-size: 17.5px;
    background-color: ${({ theme }) => theme.gray10};
`;

const StyledErrorMessage = styled.div`
    margin-bottom: 40px;
`;

const InputContainer = styled.div`
    margin-top: 20px;
    margin-bottom: 5px;
`;
const Label = styled.label`
    color: ${({ theme }) => theme['border-btn-outline-hover--blue']};
    margin-bottom: 5px;
    display: block;

    span {
        color: ${({ theme }) => theme.dangerMain};
        margin-left: 5px;
    }
`;

const StyledPrimary = styled.span`
    color: ${({ theme }) => theme.gray1} !important;
`;

const StyledDropdown = styled.div`
    .gi-dropdown__select {
        width: 80%;
    }
    .gi-dropdown {
        text-align: left;
    }
`;

const StyledSepEvents = styled.div`
    padding: 0px 8px 10px 8px;
`;

interface IModalProps extends ModalContextType {
    sepEventDetails: IShoppingWindowAllEvents;
    isEventPresent: boolean;
}

const ShoppingWindowModal = ({ closeModal, sepEventDetails, isEventPresent }: IModalProps) => {
    const { localDates } = useAppSelector(environmentSelector);
    const currentYear = useAppSelector(state => state.capApplications.currentYear);
    const { saveShoppingWindowOverride } = useGetOpenShoppingWindowDetails();
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [coverageStartDate, setCoverageStartDate] = useState<Date | null>(null);
    const [overrideComment, setOverrideComment] = useState<string>('');
    const [startDateErrorMsg, setStartDateErrorMsg] = useState<string | null>(null);
    const [endDateErrorMsg, setEndDateErrorMsg] = useState<string | null>(null);
    const [coverageStartDateErrorMsg, setCoverageStartDateErrorMsg] = useState<string | null>(null);
    const [eventValues, setEventValues] = useState<string[]>(sepEventDetails.applicants.map(() => ''));
    const { currentCoverageYear } = useAppSelector(state => state.capApplications.appControllerInfo);

    const intl = useIntl();
    const validateDates = (start: Date | null, end: Date | null) => {
        const serverDate = dayjs(localDates.serverDate);
        const isEndDateInPast = dayjs(end).isBefore(serverDate);
        const isStartBeforeEnd = start && end && validateStartBeforeEndShoppingWindow(start, end);

        const pastEndDateErrorMsg = 'cap.applications.modal.openshoppingwindow.pastEndDateErrorMsg';
        const startDateErrorMsg = 'cap.applications.modal.openshoppingwindow.startDateErrorMsg';
        const endDateErrorMsg = 'cap.applications.modal.openshoppingwindow.endDateErrorMsg';

        if (!end) {
            setStartDateErrorMsg(null);
            setEndDateErrorMsg(null);
            return;
        }

        if (!start) {
            setStartDateErrorMsg(null);
            setEndDateErrorMsg(isEndDateInPast ? pastEndDateErrorMsg : null);
            return;
        }

        if (isStartBeforeEnd) {
            setStartDateErrorMsg(null);
            setEndDateErrorMsg(isEndDateInPast ? pastEndDateErrorMsg : null);
        } else {
            setStartDateErrorMsg(startDateErrorMsg);
            setEndDateErrorMsg(isEndDateInPast ? pastEndDateErrorMsg : endDateErrorMsg);
        }
    };

    const handleStartDateChange = (date: Date) => {
        setStartDate(date);
        validateDates(date, endDate);
    };

    const handleEndDateChange = (date: Date) => {
        setEndDate(date);
        validateDates(startDate, date);
    };

    const hanldeCoverageStartDateChange = (date: Date) => {
        setCoverageStartDate(date);
        if (validateCoverageAfterPlanYear(date, currentYear || currentCoverageYear)) {
            setCoverageStartDateErrorMsg(null);
        } else {
            setCoverageStartDateErrorMsg('cap.applications.modal.openshoppingwindow.coverageStartDateErrorMsg');
        }
    };

    const handleEventValueChange = (index: number, value: string) => {
        const newEventValues = [...eventValues];
        newEventValues[index] = value;
        setEventValues(newEventValues);
    };

    const sortApplicantsByPersonId = (applicants: IShoppingWindowAllEvents['applicants']) => {
        return applicants.slice().sort((a, b) => a.personId - b.personId);
    };

    function onClose() {
        closeModal('shoppingWindowModal');
    }

    function onSave() {
        const updatedApplicants = sepEventDetails.applicants.map(applicant => ({
            ...applicant,
            events: applicant.events.map(event => ({
                ...event,
                eventId: eventValues[applicant.personId],
            })),
        }));

        const updateSepEventDetails = {
            ...sepEventDetails,
            applicants: updatedApplicants,
            sepStartDate: formatDateToMMDDYYYY(startDate),
            sepEndDate: formatDateToMMDDYYYY(endDate),
            coverageStartDate: formatDateToMMDDYYYY(coverageStartDate),
            overrideCommentText: overrideComment,
        };
        saveShoppingWindowOverride(updateSepEventDetails, isEventPresent);
    }

    function isSaveButtonDisabled() {
        const isMandatoryValues =
            !overrideComment.trim() ||
            startDateErrorMsg !== null ||
            endDateErrorMsg !== null ||
            coverageStartDateErrorMsg !== null ||
            startDate === null ||
            endDate === null ||
            coverageStartDate === null;
        const isMemberEventReason = isEventReason();
        return isMandatoryValues || isMemberEventReason;
    }

    function isEventReason() {
        let hasEventReasons = false;
        sepEventDetails?.applicants?.forEach(applicant => {
            if (eventValues[applicant.personId] === '' || eventValues[applicant.personId] === undefined) {
                hasEventReasons = true;
            }
        });
        return hasEventReasons;
    }

    const sortedApplicants = sortApplicantsByPersonId(sepEventDetails?.applicants);

    return (
        <Modal id="shopping_window_modal">
            <Modal.Header
                id="shopping_window_modal_title"
                titleId="cap.applications.modal.openshoppingwindow2"
                onHide={onClose}
            />
            <Modal.Body>
                <StyledField>
                    <ContentAlert>
                        <p data-testid="cap_application_shopping_window_modal_caution_1">
                            <FormattedMessage
                                id="cap.application.modal.openshoppingwindowcontent"
                                values={{
                                    br: <br />,
                                    strong: chunks => <strong>{chunks}</strong>,
                                }}
                            />
                        </p>
                        <p data-testid="cap_application_shopping_window_modal_caution_2">
                            <FormattedMessage id="cap.application.modal.openshoppingwindowcontent2" />
                        </p>
                        <p data-testid="cap_application_shopping_window_modal_caution_3">
                            <FormattedMessage id="cap.application.modal.openshoppingwindowcontent3" />
                        </p>
                    </ContentAlert>

                    <ConditionalRendering isTrue={isEventPresent}>
                        <StyledSepEvents data-testid="cap_application_shopping_window_modal_sep_events_content">
                            {sortedApplicants.map(applicant => (
                                <>
                                    <p>
                                        <FormattedMessage
                                            id="cap.applications.modal.openshoppingwindow.eventMessage"
                                            values={{ firstName: applicant.firstName }}
                                        />{' '}
                                        <b>{applicant.events[0].previousEventNameLabel}</b>
                                        <FormattedMessage id="cap.applications.modal.openshoppingwindow.eventMessage2" />
                                        <b>{applicant.events[0].previousEventDate}.</b>
                                        <br />
                                    </p>
                                </>
                            ))}
                        </StyledSepEvents>
                    </ConditionalRendering>

                    <StyledTable>
                        <thead>
                            <tr>
                                <th
                                    className="table-header-border-bottom1"
                                    data-testid="cap_application_shopping_window_modal_table_header_members"
                                >
                                    <FormattedMessage id="cap.application.modal.overrideSpecialEnrollmentDetailsModal.table.header.members" />
                                </th>
                                <th
                                    className="eventReasonType table-header-border-bottom2"
                                    data-testid="cap_application_shopping_window_modal_table_header_event_reason_type"
                                >
                                    <FormattedMessage id="cap.applications.modal.openshoppingwindow.eventReasonType" />
                                    <span>*</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedApplicants.map(applicant => (
                                <tr key={applicant.applicantId}>
                                    <td
                                        data-testid={`cap_application_shopping_window_modal_applicants_name_${applicant.firstName}_${applicant.lastName}`}
                                    >
                                        {applicant.firstName} {applicant.middleName} {applicant.lastName}
                                        {applicant.personId === DEFAULT_PERSON_ID && (
                                            <StyledPrimary>
                                                (<FormattedMessage id={'cap.applications.modal.primaryText'} />)
                                            </StyledPrimary>
                                        )}
                                    </td>
                                    <td
                                        data-testid={`cap_application_shopping_window_modal_applicants_dropdown_${applicant.firstName}_${applicant.lastName}`}
                                    >
                                        <StyledDropdown>
                                            <Dropdown
                                                label={{
                                                    text: `${applicant.firstName} ${intl.formatMessage({ id: 'cap.applications.modal.openshoppingwindow.eventReasonType' })}`,
                                                    labelClass: ['sr-only'],
                                                }}
                                                inputData={{
                                                    id: `cap_application_shopping_window_modal_applicants_dropdown_select_${applicant.applicantId}`,
                                                    inputClasses: ['gi-dropdown__select grid-col-4'],
                                                    name: 'shopping_window_event',
                                                    options: [
                                                        { value: '', text: '' },
                                                        ...applicant.events[0].eventList.map(event => ({
                                                            value: event.eventId,
                                                            text: event.eventNameLabel,
                                                        })),
                                                    ],
                                                    type: INPUT_TYPES.DROPDOWN,
                                                    value: eventValues[applicant.personId],
                                                    isInputRequired: false,
                                                }}
                                                inputActions={{
                                                    handleFieldChange: val =>
                                                        handleEventValueChange(applicant.personId, val),
                                                }}
                                            />
                                        </StyledDropdown>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </StyledTable>

                    <StyledBodyHeader
                        className="header-border-bottom"
                        data-testid="cap_application_shopping_window_modal_table_header_plan_shopping"
                    >
                        <FormattedMessage id="cap.applications.modal.openshoppingwindow.planShoppingWindow" />
                    </StyledBodyHeader>
                    <StyledPlanShoppingWindow>
                        <div>
                            <DatePicker
                                labelText="common.start_date"
                                wrapperClasses={['date-picker']}
                                isRequired
                                id="start_date"
                                type="text"
                                pattern="MM/DD/YYYY"
                                value={startDate}
                                withMask={true}
                                onDateChange={(date: Date) => handleStartDateChange(date)}
                            />
                        </div>

                        <div>
                            <DatePicker
                                labelText="common.end_date"
                                wrapperClasses={['date-picker']}
                                isRequired
                                id="end_date"
                                type="text"
                                pattern="MM/DD/YYYY"
                                value={endDate}
                                withMask={true}
                                onDateChange={(date: Date) => handleEndDateChange(date)}
                            />
                        </div>
                    </StyledPlanShoppingWindow>
                    <StyledErrorMessage data-testid="cap_application_shopping_window_modal_end_date_validation_error">
                        <ValidationErrorMessage isVisible={endDateErrorMsg !== null} messageText={endDateErrorMsg} />
                        <ValidationErrorMessage
                            isVisible={startDateErrorMsg !== null}
                            messageText={startDateErrorMsg}
                        />
                    </StyledErrorMessage>

                    <StyledBodyHeader
                        className="header-border-bottom"
                        data-testid="cap_application_shopping_window_modal_change_coverage_date_header"
                    >
                        <FormattedMessage id="cap.applications.modal.changecoveragedate2" />
                    </StyledBodyHeader>
                    <StyledChangeCoverageStartDate>
                        <DatePicker
                            labelText="cap.applications.modal.coverageStartDate"
                            wrapperClasses={['date-picker']}
                            isRequired
                            id="coverage_start_date"
                            type="text"
                            pattern="MM/DD/YYYY"
                            value={coverageStartDate}
                            withMask={true}
                            onDateChange={(date: Date) => hanldeCoverageStartDateChange(date)}
                        />
                    </StyledChangeCoverageStartDate>
                    <StyledErrorMessage data-testid="cap_application_shopping_window_modal_start_date_validation_error">
                        <ValidationErrorMessage
                            isVisible={coverageStartDateErrorMsg !== null}
                            messageText={coverageStartDateErrorMsg}
                        />
                    </StyledErrorMessage>

                    <StyledBodyHeader
                        className="header-border-bottom"
                        data-testid="cap_application_shopping_window_modal_override_reason_header"
                    >
                        <FormattedMessage id="cap.applications.modal.overrideReason" />
                    </StyledBodyHeader>
                    <InputContainer>
                        <Label
                            data-testid="cap_application_shopping_window_modal_override_reason_input_label"
                            htmlFor="shopping_window_modal_textarea"
                        >
                            <FormattedMessage id="cap.applications.modal.openshoppingwindowReason" />
                            <span>*</span>
                        </Label>
                        <CommonTextArea
                            id="shopping_window_modal_textarea"
                            value={overrideComment}
                            setValue={(value: string) => value.length <= INPUT_MAX_LENGTH && setOverrideComment(value)}
                            placeholder="cap.applications.modal.explainPlaceholder"
                            hoverText={true}
                        />
                    </InputContainer>
                    <div data-testid="cap_application_shopping_window_modal_max_chars_left">
                        {INPUT_MAX_LENGTH - overrideComment.trim().length} {''}
                        <FormattedMessage id="cap.applications.modal.charactersLeft" />
                    </div>
                </StyledField>
            </Modal.Body>
            <StyledModalControls>
                <Modal.Controls>
                    <StyledControls>
                        <GIButton
                            id="cap_application_shopping_window_modal_save_button"
                            onClickHandler={onSave}
                            isDisabled={isSaveButtonDisabled()}
                            variant="primary"
                            labelId="button.save"
                        />
                        <GIButton
                            id="cap_application_shopping_window_modal_cancel_button"
                            onClickHandler={onClose}
                            variant="secondary"
                            labelId="button.cancel"
                        />
                    </StyledControls>
                </Modal.Controls>
            </StyledModalControls>
        </Modal>
    );
};

export default ShoppingWindowModal;
